import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Box,
    LinearProgress,
    Paper
} from '@mui/material';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import SentimentSatisfiedAltRoundedIcon from '@mui/icons-material/SentimentSatisfiedAltRounded';
import { DataGrid } from '@mui/x-data-grid';

import BiMetric from '../../components/biMetric';
import MonthDatePicker from '../../components/monthDatePicker';
import PageHeader from '../../components/pageHeader';
import { PageIntro } from '../../components/ui';
import {
    formatBackendDatePtBr,
    formatMonthParam,
    getCurrentMonthStart,
    parseBackendDate
} from '../../utils/dateUtils';
import {
    formatDecimal,
    formatInteger,
    formatPercentRatio
} from '../../utils/numberUtils';
import {
    buildNpsDistribution,
    buildNpsRows,
    buildNpsSummary,
    fetchNpsMes
} from './npsData';
import '../css/biAluguelAccess.css';

const DistributionChart = ({ rows }) => {
    const [activeRow, setActiveRow] = useState(null);
    const maxCount = Math.max(...rows.map((row) => row.count), 1);

    return (
        <Paper className='bi-aluguel-access-panel bi-ge-nps-distribution-panel'>
            <div className='bi-aluguel-access-panel-heading'>
                <div>
                    <p>Notas 1-10</p>
                    <h2>Histograma</h2>
                </div>
                <BarChartRoundedIcon />
            </div>

            <div className='bi-ge-nps-distribution-chart' role='img' aria-label='Distribuição das notas de NPS'>
                {rows.map((row) => (
                    <div
                        className='bi-ge-nps-distribution-column'
                        key={row.score}
                        tabIndex='0'
                        aria-label={`Nota ${row.score}: ${formatInteger(row.count)} respostas`}
                        onBlur={() => setActiveRow(null)}
                        onFocus={() => setActiveRow(row)}
                        onMouseEnter={() => setActiveRow(row)}
                        onMouseLeave={() => setActiveRow(null)}
                    >
                        <span>{formatPercentRatio(row.percentage)}</span>
                        <div className='bi-ge-nps-distribution-track'>
                            <div
                                className='bi-ge-nps-distribution-bar'
                                style={{ '--bar-size': `${Math.max((row.count / maxCount) * 100, row.count ? 8 : 0)}%` }}
                            />
                        </div>
                        <strong>{row.score}</strong>
                    </div>
                ))}
                {activeRow && (
                    <div className='bi-ge-nps-distribution-tooltip'>
                        <span>Nota {activeRow.score}</span>
                        <strong>{formatInteger(activeRow.count)} respostas</strong>
                        <small>{formatPercentRatio(activeRow.percentage)}</small>
                    </div>
                )}
            </div>
        </Paper>
    );
};

const emptyRowsLabel = 'Nenhuma resposta encontrada para o mês selecionado.';
const formatIdentifier = (value) => value == null ? '' : String(value);

const NpsBIGE = () => {
    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthStart);
    const [nps, setNps] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadNps = useCallback(async (monthValue) => {
        setIsLoading(true);
        const mes = formatMonthParam(monthValue);
        const monthNps = await fetchNpsMes(mes);
        setNps(monthNps);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        loadNps(selectedMonth);
    }, [loadNps, selectedMonth]);

    const summary = useMemo(() => buildNpsSummary(nps), [nps]);
    const distribution = useMemo(() => buildNpsDistribution(nps), [nps]);
    const rows = useMemo(() => buildNpsRows(nps), [nps]);

    const columns = useMemo(() => [
        {
            field: 'clienteId',
            headerName: 'ID',
            type: 'number',
            width: 80,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (value) => formatIdentifier(value)
        },
        {
            field: 'cliente',
            headerName: 'Cliente',
            minWidth: 260,
            flex: 1.2
        },
        {
            field: 'dataPesquisa',
            headerName: 'Data da pesquisa',
            minWidth: 170,
            flex: 0.7,
            valueGetter: (value) => parseBackendDate(value),
            valueFormatter: (value) => formatBackendDatePtBr(value)
        },
        {
            field: 'nota',
            headerName: 'Nota',
            type: 'number',
            width: 100,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'comentario',
            headerName: 'Comentário',
            minWidth: 280,
            flex: 1.35
        }
    ], []);

    return (
        <Box className='mainBox bi-aluguel-access-page bi-ge-indications-page bi-ge-nps-page'>
            <PageHeader>BI Aluguel - NPS</PageHeader>

            <PageIntro eyebrow='BI Aluguel' title='NPS'>
                Respostas da pesquisa de satisfação no mês e a nota média dos clientes.
            </PageIntro>

            <section className='bi-aluguel-access-shell bi-ge-indications-shell'>
                {isLoading && <LinearProgress className='bi-aluguel-access-loading' />}

                <div className='bi-ge-nps-summary-row'>
                    <div className='bi-aluguel-access-filters bi-ge-indications-filters bi-ge-nps-filters'>
                        <MonthDatePicker
                            value={selectedMonth}
                            disabled={isLoading}
                            onChange={setSelectedMonth}
                        />
                    </div>

                    <div className='bi-aluguel-access-metrics bi-ge-indications-metrics bi-ge-nps-metrics'>
                        <BiMetric
                            icon={GroupsRoundedIcon}
                            label='Total de respondentes'
                            value={formatInteger(summary.totalRespondents)}
                            subtitle='Respostas no mês'
                            tone='green'
                        />
                        <BiMetric
                            icon={SentimentSatisfiedAltRoundedIcon}
                            label='CSAT geral'
                            value={formatDecimal(summary.csatScore)}
                            subtitle={`Meta ${formatInteger(summary.target)}`}
                            tone='blue'
                        />
                        <BiMetric
                            icon={FactCheckRoundedIcon}
                            label='Nota média'
                            value={formatDecimal(summary.averageScore)}
                            subtitle='Escala de 1 a 10'
                        />
                    </div>
                </div>

                <div className='bi-ge-nps-content-grid'>
                    <DistributionChart rows={distribution} />

                    <Paper className='bi-aluguel-access-panel bi-aluguel-access-table-panel bi-ge-nps-table-panel'>
                        <div className='bi-aluguel-access-panel-heading'>
                            <div>
                                <p>Respostas filtradas</p>
                                <h2>Lista de pesquisas</h2>
                            </div>
                            <AssessmentRoundedIcon />
                        </div>

                        <div className='bi-aluguel-access-grid-shell'>
                            <DataGrid
                                className='bi-aluguel-access-grid'
                                rows={rows}
                                columns={columns}
                                loading={isLoading}
                                disableRowSelectionOnClick
                                localeText={{ noRowsLabel: emptyRowsLabel }}
                                pageSizeOptions={[25, 50, 100]}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 50
                                        }
                                    },
                                    sorting: {
                                        sortModel: [{ field: 'dataPesquisa', sort: 'desc' }]
                                    }
                                }}
                            />
                        </div>
                    </Paper>
                </div>
            </section>
        </Box>
    );
};

export default NpsBIGE;
